import { maxUtf8ChunksForByteLength } from "./ChunkSender";

/**
 * Per-chunk allowance added to a chunked call's deadline.
 *
 * Each chunk costs a bridge crossing plus a JSON parse on both ends, and the
 * sender yields to the event loop periodically, so the cost of a large message
 * is roughly linear in chunk count. 60ms is deliberately generous against the
 * ~12.5ms a chunk actually needs — the deadline exists to catch a hung bridge,
 * not to police throughput, and a false timeout on a healthy large transfer is
 * far more damaging than a slow one.
 */
const PER_CHUNK_ALLOWANCE_MS = 60;

export function chunkTransferAllowanceMs(chunkCount: number): number {
  return Math.max(0, chunkCount) * PER_CHUNK_ALLOWANCE_MS;
}

/**
 * Deadline for an outbound call, in milliseconds.
 *
 * An unchunked message keeps the caller's base deadline. A chunked one gets an
 * allowance per chunk on top: at the base alone the advertised maxTransferSize
 * is unreachable by construction, so a caller sending a large payload would
 * always see a timeout regardless of how healthy the transport is.
 *
 * Callers passing an explicit timeout are not overridden — this only supplies
 * the default.
 */
export function callDeadlineMs(
  serializedBytes: number,
  chunkThreshold: number,
  chunkSize: number,
  baseMs: number,
): number {
  if (serializedBytes <= chunkThreshold || chunkSize <= 0) {
    return baseMs;
  }
  const chunks = maxUtf8ChunksForByteLength(serializedBytes, chunkSize);
  return baseMs + chunkTransferAllowanceMs(chunks);
}
